import React, { useEffect } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents, ScaleControl, Rectangle, ImageOverlay } from "react-leaflet"; // Add Rectangle
import { RasterLayerComponent } from "../Layers/RasterLayerComponent";
import { rasterLayers, field1Bounds, getVisibleImageryLayers, getVisibleDetectionLayers } from "../Layers/index"; // Import field1Bounds
import { Map as LeafletMap } from 'leaflet'; // Import Leaflet Map type
import { Layer } from '../../types'; // Import Layer type
import MouseCoordinatesDisplay from './MouseCoordinatesDisplay'; // Import the new display component
import L from 'leaflet';
import LandmineMarkers from '../Layers/LandmineMarkers'; // Import LandmineMarkers

const ZoomDebugger: React.FC<{ layers: Layer[] }> = ({ layers }) => {
  const map = useMap();

  useMapEvents({
    zoomend: (e) => {
      const currentZoom = e.target.getZoom();
      console.log(`=== ZOOM DEBUG ===`);
      console.log(`Current zoom level: ${currentZoom}`);
      console.log(`Map bounds:`, map.getBounds());

      // Check each layer's zoom constraints and visibility
      layers.forEach(layer => {
        const withinZoomRange = currentZoom >= (layer.minZoom || 0) && currentZoom <= (layer.maxZoom || 30);
        console.log(`Layer ${layer.id}:`, {
          showImagery: layer.showImagery,
          showDetections: layer.showDetections,
          hasImagery: layer.hasImagery,
          hasDetections: layer.hasDetections,
          withinZoomRange,
          minZoom: layer.minZoom,
          maxZoom: layer.maxZoom,
          currentZoom
        });
      });
    }
  });

  return null;
};

interface MapProps {
  viewState: {
    longitude: number;
    latitude: number;
    zoom: number;
  };
  setViewState: React.Dispatch<React.SetStateAction<{
    longitude: number;
    latitude: number;
    zoom: number;
  }>>;
  mapInstance: LeafletMap | null; // Use LeafletMap type
  setMapInstance: React.Dispatch<React.SetStateAction<LeafletMap | null>>; // Use LeafletMap type
  layers: Layer[]; // New unified layer system
  selectedField: number;
  FIELDCENTER: [number, number];
  mouseCoordinates: { lat: number; lng: number } | null; // Add props for mouse coordinates
  setMouseCoordinates: React.Dispatch<React.SetStateAction<{ lat: number; lng: number } | null>>; // Add props for mouse coordinates
}
// Component to log zoom level changes
const ZoomLogger: React.FC = () => {
  const map = useMap();

  // Log initial zoom level when component mounts
  useEffect(() => {
    if (map) {
      console.log(`Current zoom level: ${map.getZoom()}`);
    }
  }, [map]);

  // Log zoom level whenever it changes
  useMapEvents({
    zoomend: (e) => {
      const currentZoom = e.target.getZoom();
      console.log(`Current zoom level: ${currentZoom}`);
    }
  });

  return null; // This component doesn't render anything
};

// Component to handle map events like move and load
const MapEventsHandler: React.FC<{
  setViewState: MapProps['setViewState'];
  setMapInstance: MapProps['setMapInstance'];
  setMouseCoordinates: MapProps['setMouseCoordinates']; // Add prop for setting coordinates
}> = ({ setViewState, setMapInstance, setMouseCoordinates }) => {
  const map = useMap();

  useEffect(() => {
    // Set map instance once when the map is ready
    setMapInstance(map);
  }, [map, setMapInstance]);

  useMapEvents({
    // Update view state when map is moved (pan or zoom)
    moveend: (e) => {
      const center = e.target.getCenter();
      const zoom = e.target.getZoom();
      setViewState({
        latitude: center.lat,
        longitude: center.lng,
        zoom: zoom
      });
      console.log(`Map moved to: ${center.lat.toFixed(6)}, ${center.lng.toFixed(6)}, zoom: ${zoom}`);
    },

    // Add mousemove event listener
    mousemove: (e) => {
      setMouseCoordinates({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
    // Clear coordinates when mouse leaves map
    mouseout: () => {
      setMouseCoordinates(null);
    }
  });

  return null; // This component doesn't render anything
};

const MapComponent: React.FC<MapProps> = ({
  viewState,
  setViewState,
  mapInstance,
  setMapInstance,
  layers,
  selectedField,
  FIELDCENTER,
  mouseCoordinates, // Destructure new props
  setMouseCoordinates // Destructure new props
}) => {
  const basemapUrl = process.env.REACT_APP_TILE_URL + "/basemap/usda/{z}/{x}/{y}.webp";
  const basemapAttribution = "U. S. Department of Agriculture, Farm Service Agency";

  const mapMinZoom = parseInt((process.env.REACT_APP_MIN_ZOOM || '18') as string, 10);
  const mapMaxZoom = parseInt((process.env.REACT_APP_MAX_ZOOM || '28') as string, 10);
  // 36.34807, -96.85313
  const corner1 = L.latLng(36.346008, -96.868799);
  const corner2 = L.latLng(36.358878, -96.850692);
  const basemapBounds = L.latLngBounds(corner1, corner2);

  // Get layers that should be visible for imagery
  const visibleImageryLayers = getVisibleImageryLayers(layers);
  const visibleDetectionLayers = getVisibleDetectionLayers(layers);

  // Add useMemo to prevent unnecessary re-renders of RasterLayerComponents
  const memoizedRasterLayers = React.useMemo(() => {
    if (!mapInstance) return null;

    return visibleImageryLayers.map(layer => {
      // Convert Layer to RasterLayer format for backward compatibility
      const rasterLayer = {
        id: layer.id,
        name: layer.name,
        url: layer.tileUrl || '',
        field: layer.field,
        instrument: layer.sensorType.toLowerCase().includes('rgb') ? 'Ortho' as const :
                   layer.sensorType.toLowerCase().includes('thermal') ? 'TIR' as const :
                   'Spectral' as const,
        timePoint: layer.burialTime.toLowerCase().includes('preburial') ? 'PreBurial' as const :
                  layer.burialTime.toLowerCase().includes('postburial') ? 'PostBurial' as const :
                  'Other' as const,
        visible: layer.showImagery,
        opacity: layer.opacity || 1.0,
        minZoom: layer.minZoom,
        maxZoom: layer.maxZoom,
        minNativeZoom: layer.minNativeZoom,
        maxNativeZoom: layer.maxNativeZoom,
        bounds: layer.bounds,
        date: layer.dateCollected
      };
      
      return (
        <RasterLayerComponent
          key={layer.id}
          layer={rasterLayer}
          visible={true} // Already filtered for visible layers
        />
      );
    });
  }, [mapInstance, visibleImageryLayers]); // Only re-create when these dependencies change

  return (
    <MapContainer
      center={[viewState.latitude, viewState.longitude]} // Leaflet uses [lat, lng]
      zoom={20}
      style={{ width: '100%', height: '100vh' }}
      minZoom={mapMinZoom}
      maxZoom={mapMaxZoom} // Updated to match overlay maxZoom
      maxBounds={basemapBounds}>
      {/* Basemap Layer
      <ImageOverlay
        bounds={basemapBounds}
        url="http://0.0.0.0:8000/data/basemap/usda.png"
        attribution={basemapAttribution}
      /> */}
      <ZoomDebugger layers={layers} />
      <TileLayer
        url={basemapUrl}
        attribution={basemapAttribution}
        tileSize={512}
        zoomOffset={-1}
        minZoom={mapMinZoom}
        maxZoom={mapMaxZoom} // Updated to match overlay maxZoom
        minNativeZoom={15}
        maxNativeZoom={19}
        crossOrigin="anonymous"
      />

      {/* Add the Scale Control */}
      <ScaleControl position="bottomleft" metric={true} imperial={true} />

      {/* Component to handle map events and set instance */}
      <MapEventsHandler
        setViewState={setViewState}
        setMapInstance={setMapInstance}
        setMouseCoordinates={setMouseCoordinates} // Pass setter to handler
      />

      {/* Display the field1Bounds as a rectangle */}
      {/* <Rectangle 
        bounds={field1Bounds}
        pathOptions={{ 
          color: 'red',
          weight: 2,
          fillOpacity: 0.2
        }}
      /> */}

      {/* Render landmine markers for layers that have detections visible */}
      {visibleDetectionLayers.map(layer => (
        <LandmineMarkers 
          key={`markers-${layer.id}`}
          selectedField={layer.field} 
          layerId={layer.datasetId}
          visible={true} 
        />
      ))}

      {memoizedRasterLayers}

      {/* Render the coordinates display */}
      <MouseCoordinatesDisplay coordinates={mouseCoordinates} />

      <ZoomLogger />
    </MapContainer>
  );
};

export default MapComponent;
